import { useCallback, useContext, useMemo } from 'react';
import produce from 'immer';
import {
  EditorDeclarativeBlock,
  TargetingDeviceType,
  toTargetingDeviceType,
} from '@/webapi/use-experience-api';
import { DeviceType } from '@/utils/definitions';
import { shouldSync } from '@/utils/experience-utils';
import { getScreenshotFromChange } from '@/utils/change-screenshot';
import { AccountContext } from '@/features/account-context';
import { EditorContext } from '@/features/editor/context/editor-context';
import { AutomationChange } from '@/pkg/sdk';

export interface UseChangelogTileProps {
  change: EditorDeclarativeBlock;
  device: DeviceType;
  onClick?: () => void;
}

export function useChangelogTile({
  change,
  device,
  onClick,
}: UseChangelogTileProps) {
  const {
    account: {
      store: { alias },
    },
  } = useContext(AccountContext);

  const {
    resources: { appsCatalog },
    experienceState: { currentExperience, upsertEditorChange },
    devicePreview: { automatePlay },
  } = useContext(EditorContext);

  const isSynced = useMemo(
    () => shouldSync(currentExperience, change),
    [currentExperience, change],
  );

  const screenshot = useMemo(
    () =>
      getScreenshotFromChange(
        alias,
        currentExperience.id,
        currentExperience.variants[0],
        change,
      ),
    [alias, currentExperience, change],
  );

  const onBeforeClick = useCallback(() => {
    if (isSynced && onClick) onClick();
  }, [isSynced, onClick]);

  const onAutomationPlay = useCallback(
    (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      automatePlay((change?.block?.value as AutomationChange).steps);
    },
    [automatePlay, change],
  );

  const onToggleVisibility = useCallback(() => {
    const temp = produce(change, (draft) => {
      draft.isDisabled = !draft?.isDisabled;
    });

    upsertEditorChange(temp);
  }, [change, upsertEditorChange]);

  const shouldShowVisibilityToggle = useMemo(() => {
    if (change.targetDevice === TargetingDeviceType.ALL_DEVICES) {
      return true;
    }
    return toTargetingDeviceType(device) === change.targetDevice;
  }, [device, change]);

  return {
    isSynced,
    screenshot,
    onBeforeClick,
    onAutomationPlay,
    onToggleVisibility,
    shouldShowVisibilityToggle,
    appsCatalog,
  };
}
